var _a;
import { EventDispatcher } from "../event-dispatcher.js";
class CustomElementWithTemplateNode extends HTMLElement {
    constructor() {
        super();
        this.alreadyConnected = false;
        const shadowRoot = this.attachShadow({ mode: "open" });
        const clonedNode = document.importNode(this.getTemplateNode().content, true);
        shadowRoot.appendChild(clonedNode);
    }
    connectedCallback() {
        if (this.alreadyConnected) {
            return;
        }
        this.alreadyConnected = true;
        this.setAttribute("data-has-shadow-dom", "true");
    }
    getTemplateNode() {
        throw Error("Must override this method");
    }
    static setTemplateNode(_templateNode) {
        throw Error("Must override this method");
    }
}
export class StyledSlider extends CustomElementWithTemplateNode {
    constructor() {
        super();
        const shadowRoot = this.shadowRoot;
        this.slider = shadowRoot.getElementById("slider");
    }
    updateStyle() {
        const slider = this.slider;
        const min = Number(slider.min);
        const max = Number(slider.max);
        let valuePercent = ((slider.valueAsNumber - min) / (max - min)) * 100;
        if (!Number.isFinite(valuePercent)) {
            valuePercent = 0;
        }
        this.slider.style.setProperty("--track-fill-percent", valuePercent + "%");
    }
    connectedCallback() {
        if (this.alreadyConnected) {
            return;
        }
        super.connectedCallback();
        const slider = this.slider;
        const defaultValue = this.getAttribute("data-default-value");
        const minValue = this.getAttribute("data-min-value");
        const maxValue = this.getAttribute("data-max-value");
        const step = this.getAttribute("data-step");
        if (minValue !== null) {
            slider.min = minValue;
        }
        if (maxValue !== null) {
            slider.max = maxValue;
        }
        if (step !== null) {
            slider.step = step;
        }
        if (defaultValue !== null) {
            slider.value = defaultValue;
        }
        this.updateStyle();
        slider.addEventListener("input", () => this.updateStyle());
        // The slider background track's size must be updated whenever it changes, both when setting it from the UI,
        // and also when setting it from script (using "set value" or "set valueAsNumber")
        // This is a hack for intercepting all value changes, by overriding the setter function to also update the style
        // If anyone finds a better solution, feel free to change this
        const overrideProperty = (prop) => {
            const original = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, prop);
            if (!original) {
                return;
            }
            const originalGetter = original.get;
            const originalSetter = original.set;
            const overrides = {};
            if (originalSetter) {
                const setter = originalSetter.bind(slider);
                overrides.set = (v) => {
                    setter(v);
                    this.updateStyle();
                };
            }
            if (originalGetter) {
                const getter = originalGetter.bind(slider);
                overrides.get = getter;
            }
            Object.defineProperty(slider, prop, overrides);
        };
        ["value", "valueAsNumber"].forEach(overrideProperty);
    }
    getTemplateNode() {
        return StyledSlider.templateNode;
    }
    static setTemplateNode(templateNode) {
        this.templateNode = templateNode;
    }
}
var StyledSliderWithInputUpdateMode;
(function (StyledSliderWithInputUpdateMode) {
    StyledSliderWithInputUpdateMode[StyledSliderWithInputUpdateMode["All"] = 0] = "All";
    StyledSliderWithInputUpdateMode[StyledSliderWithInputUpdateMode["SliderInputChanged"] = 1] = "SliderInputChanged";
    StyledSliderWithInputUpdateMode[StyledSliderWithInputUpdateMode["TextInputChanged"] = 2] = "TextInputChanged";
})(StyledSliderWithInputUpdateMode || (StyledSliderWithInputUpdateMode = {}));
export class StyledSliderWithInput extends CustomElementWithTemplateNode {
    constructor() {
        super();
        this.defaultValue = null;
        this.minValue = null;
        this.maxValue = null;
        this.numberStep = null;
        this.displayedDecimals = 0;
        this.onChange = new EventDispatcher();
        const shadowRoot = this.shadowRoot;
        this.textInput = shadowRoot.getElementById("text-input");
        this.styledSlider = shadowRoot.getElementById("slider-input");
        this.sliderInput = this.styledSlider.shadowRoot.getElementById("slider");
        this.stepDownButton = shadowRoot.getElementById("step-down");
        this.stepUpButton = shadowRoot.getElementById("step-up");
        this.stepDownButton.classList.add("button-with-border");
        this.stepUpButton.classList.add("button-with-border");
        this.textInput.addEventListener("change", () => {
            const isEmpty = this.textInput.value === "";
            const value = isEmpty ? NaN : Number(this.textInput.value);
            this.setValues(value, 2 /* StyledSliderWithInputUpdateMode.TextInputChanged */, true);
            this.textInput.value = this.sliderInput.value;
        });
        this.sliderInput.addEventListener("input", () => {
            this.setValues(this.sliderInput.valueAsNumber, 1 /* StyledSliderWithInputUpdateMode.SliderInputChanged */, true);
        });
        this.stepUpButton.addEventListener("click", () => {
            if (this.numberStep !== null) {
                this.setValues(this.sliderInput.valueAsNumber + this.numberStep, 0 /* StyledSliderWithInputUpdateMode.All */, true);
            }
        });
        this.stepDownButton.addEventListener("click", () => {
            if (this.numberStep !== null) {
                this.setValues(this.sliderInput.valueAsNumber - this.numberStep, 0 /* StyledSliderWithInputUpdateMode.All */, true);
            }
        });
    }
    setup() {
        var _a;
        const shadowRoot = this.shadowRoot;
        const getAttributeAsNumber = (attribute) => {
            const attr = this.getAttribute(attribute);
            if (attr === null) {
                return null;
            }
            const value = Number(attr);
            if (!Number.isFinite(value)) {
                return null;
            }
            return value;
        };
        this.defaultValue = getAttributeAsNumber("data-default-value");
        this.minValue = getAttributeAsNumber("data-min-value");
        this.maxValue = getAttributeAsNumber("data-max-value");
        const step = this.getAttribute("data-step");
        const stepButtonStep = getAttributeAsNumber("data-step-button-step");
        const suffix = (_a = this.getAttribute("data-suffix")) !== null && _a !== void 0 ? _a : "";
        if (this.minValue !== null) {
            const str = this.minValue.toString();
            this.textInput.min = str;
            this.sliderInput.min = str;
            shadowRoot.getElementById("min-value-label").textContent = str + suffix;
        }
        if (this.maxValue !== null) {
            const str = this.maxValue.toString();
            this.textInput.max = str;
            this.sliderInput.max = str;
            shadowRoot.getElementById("max-value-label").textContent = str + suffix;
        }
        if (this.defaultValue !== null) {
            const str = this.defaultValue.toString();
            this.textInput.value = str;
            this.sliderInput.valueAsNumber = this.defaultValue;
        }
        let hasValidStep = false;
        if (step !== null) {
            this.textInput.step = step;
            this.sliderInput.step = step;
            if (step === "any") {
                hasValidStep = true;
                this.displayedDecimals = null;
            }
            else {
                let numberStep = Number(step);
                if (!isNaN(numberStep) && numberStep >= 0) {
                    hasValidStep = true;
                    const maxDecimals = 10;
                    let currentDecimals = 0;
                    for (let i = 0; i < maxDecimals; ++i) {
                        if (1 / numberStep <= 1) {
                            break;
                        }
                        ++currentDecimals;
                        numberStep *= 10;
                    }
                    this.displayedDecimals = currentDecimals;
                }
            }
            if (stepButtonStep) {
                let numberStep = Number(stepButtonStep);
                if (!isNaN(numberStep) && numberStep >= 0) {
                    this.numberStep = numberStep;
                    this.stepDownButton.style.display = "";
                    this.stepUpButton.style.display = "";
                }
            }
        }
        if (!hasValidStep) {
            this.displayedDecimals = 0;
        }
        this.styledSlider.updateStyle();
    }
    setDisabled(disabled) {
        this.textInput.disabled = disabled;
        this.sliderInput.disabled = disabled;
    }
    setWarningStyle(warning) {
        if (warning) {
            this.sliderInput.classList.add("warning");
        }
        else {
            this.sliderInput.classList.remove("warning");
        }
    }
    getTemplateNode() {
        return StyledSliderWithInput.templateNode;
    }
    static setTemplateNode(templateNode) {
        this.templateNode = templateNode;
    }
    setValue(value, fireEvent = true) {
        this.setValues(value, 0 /* StyledSliderWithInputUpdateMode.All */, fireEvent);
    }
    setValues(value, mode, fireEvent) {
        var _a;
        if (isNaN(value)) {
            value = (_a = this.defaultValue) !== null && _a !== void 0 ? _a : 0;
        }
        if (this.minValue !== null && value < this.minValue) {
            value = this.minValue;
        }
        if (this.maxValue !== null && value > this.maxValue) {
            value = this.maxValue;
        }
        const valueString = this.displayedDecimals === null || Number.isInteger(value)
            ? value.toString()
            : value.toFixed(this.displayedDecimals);
        if (mode === 0 /* StyledSliderWithInputUpdateMode.All */ ||
            mode == 1 /* StyledSliderWithInputUpdateMode.SliderInputChanged */) {
            // Update text input
            this.textInput.value = valueString;
        }
        if (mode === 0 /* StyledSliderWithInputUpdateMode.All */ || mode == 2 /* StyledSliderWithInputUpdateMode.TextInputChanged */) {
            // Update slider input
            this.sliderInput.valueAsNumber = value;
        }
        if (fireEvent) {
            this.onChange.dispatchEvent(this.sliderInput.valueAsNumber);
        }
    }
}
const customElementMap = new Map([
    ["styled-slider", StyledSlider],
    ["styled-slider-with-input", StyledSliderWithInput],
]);
// Collect templates, register custom elements
for (const templateNode of document.querySelectorAll("template")) {
    const tagName = templateNode.getAttribute("data-tag-name");
    if (tagName === null) {
        continue;
    }
    const CustomElementClass = (_a = customElementMap.get(tagName)) !== null && _a !== void 0 ? _a : class extends CustomElementWithTemplateNode {
        getTemplateNode() {
            return templateNode;
        }
        static setTemplateNode(_templateNode) {
            // Nothing to do here
        }
    };
    CustomElementClass.setTemplateNode(templateNode);
    window.customElements.define(tagName, CustomElementClass);
}
export function QuerySelectorAllIncludingShadowDom(selector, root = document.body) {
    const resultElements = [];
    function VisitRoot(elem) {
        resultElements.push(...elem.querySelectorAll(selector));
        for (const shadowRootParent of elem.querySelectorAll("[data-has-shadow-dom=true]")) {
            if (shadowRootParent.shadowRoot !== null) {
                VisitRoot(shadowRootParent.shadowRoot);
            }
        }
    }
    VisitRoot(root);
    return resultElements;
}
export function InitializeCustomElements() {
    // Nothing, just to export the module
}
//# sourceMappingURL=custom-elements.js.map